import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './PreparationCreatorModal.css';
import { StandardSearch, performSmartSearch } from '../styles/standardStyles';
import { sortBySearchPriority } from '../utils/searchPriority';
import InventoryService from '../services/inventoryService';
import { formatQuantity, autoScaleToSymbol } from '../utils/unitDisplay';
import RecipesService from '../services/recipesService';
import { FaChevronDown, FaTimes } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPenToSquare, faUndo, faRedo, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const getUnitShortform = (unit) => {
  if (!unit) return '';
  const name = (unit.unit_name || '').toLowerCase();
  const symbol = (unit.unit_symbol || '').toLowerCase();
  
  if (symbol === 'kg' || name === 'kilogram') return 'kg';
  if (symbol === 'g' || name === 'gram') return 'g';
  if (symbol === 'l' || symbol === 'L' || name === 'liter' || name === 'litre') return 'L';
  if (symbol === 'ml' || name === 'milliliter') return 'ml';
  if (symbol === 'pcs' || symbol === 'pc' || name === 'piece' || name === 'pieces') return 'pcs';
  
  return unit.unit_symbol || unit.unit_name || '';
};

const PreparationCreatorModal = ({ businessId, onSave, onCancel }) => {
  const [step, setStep] = useState(0); // 0 = List, 1 = Info, 2 = Ingredients
  
  // Step 0: Existing Preparations
  const [existingPreparations, setExistingPreparations] = useState([]);
  const [loadingPreparations, setLoadingPreparations] = useState(true);
  const [preparationSearchQuery, setPreparationSearchQuery] = useState('');
  const preparationSearchInputRef = useRef(null);
  
  // Edit Mode
  const [editingPreparationId, setEditingPreparationId] = useState(null);
  const [activeReviewDraftKey, setActiveReviewDraftKey] = useState(null);
  const [inlineEditingPreparationId, setInlineEditingPreparationId] = useState(null);
  const [showDeletePreparationModal, setShowDeletePreparationModal] = useState(false);
  const [preparationToDelete, setPreparationToDelete] = useState(null);
  const [deletingPreparation, setDeletingPreparation] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    mode: 'alert',
    title: '',
    message: '',
    confirmLabel: 'OK',
    cancelLabel: 'Cancel'
  });
  const feedbackActionRef = useRef(null);

  // Unit mismatch warning popup
  const [showUnitWarning, setShowUnitWarning] = useState(false);
  const [unitWarningItems, setUnitWarningItems] = useState([]);

  const closeFeedbackModal = () => {
    feedbackActionRef.current = null;
    setFeedbackModal((previous) => ({
      ...previous,
      isOpen: false
    }));
  };

  const openFeedbackAlert = (title, message, confirmLabel = 'OK') => {
    feedbackActionRef.current = null;
    setFeedbackModal({
      isOpen: true,
      mode: 'alert',
      title,
      message,
      confirmLabel,
      cancelLabel: 'Cancel'
    });
  };

  const openFeedbackConfirm = ({
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onConfirm
  }) => {
    feedbackActionRef.current = onConfirm;
    setFeedbackModal({
      isOpen: true,
      mode: 'confirm',
      title,
      message,
      confirmLabel,
      cancelLabel
    });
  };

  const handleFeedbackConfirm = async () => {
    const confirmAction = feedbackActionRef.current;

    if (feedbackModal.mode !== 'confirm' || !confirmAction) {
      closeFeedbackModal();
      return;
    }

    closeFeedbackModal();
    await confirmAction();
  };
  
  const handleDeletePreparation = (e, prep) => {
    e.stopPropagation(); // Prevent row click (edit)
    setPreparationToDelete(prep);
    setShowDeletePreparationModal(true);
  };

  const confirmDeletePreparation = async () => {
    if (!preparationToDelete?.id) return;

    const localDraftKey = preparationToDelete?.draft_key || (
      typeof preparationToDelete.id === 'string' && String(preparationToDelete.id).startsWith('new:')
        ? String(preparationToDelete.id)
        : null
    );

    if (localDraftKey) {
      clearReviewDraftByKey(localDraftKey);
      if (inlineEditingPreparationId === preparationToDelete.id) {
        setInlineEditingPreparationId(null);
        setEditingPreparationId(null);
        setActiveReviewDraftKey(null);
        setPreparationName('');
        setTotalQuantity('');
        clearIngredientEditorState();
      }
      setShowDeletePreparationModal(false);
      setPreparationToDelete(null);
      return;
    }

    try {
      setDeletingPreparation(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
      const response = await fetch(`${apiUrl}/medium-features/decomposition/templates/${preparationToDelete.id}?businessId=${businessId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        clearReviewDraftForPreparation(preparationToDelete.id);
        if (inlineEditingPreparationId === preparationToDelete.id) {
          setInlineEditingPreparationId(null);
          setEditingPreparationId(null);
          setPreparationName('');
          setTotalQuantity('');
          clearIngredientEditorState();
        }
        await loadExistingPreparations();
        setShowDeletePreparationModal(false);
        setPreparationToDelete(null);
      } else {
        const err = await response.json();
        openFeedbackAlert('Failed to Delete Preparation', err.error || 'Failed to delete.');
      }
    } catch (error) {
      console.error('Delete preparation error:', error);
      openFeedbackAlert('Failed to Delete Preparation', 'Failed to delete preparation.');
    } finally {
      setDeletingPreparation(false);
    }
  };

  // Step 1: Basic Information
  const [preparationName, setPreparationName] = useState('');
  const [totalQuantity, setTotalQuantity] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  
  // Step 2: Ingredients
  const [ingredients, setIngredients] = useState([]);
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [importingMenu, setImportingMenu] = useState(false);
  const [importingPreparation, setImportingPreparation] = useState(false);
  
  // UI State
  const [saving, setSaving] = useState(false);
  const [searchTerms, setSearchTerms] = useState({});
  const [showDropdowns, setShowDropdowns] = useState({});
  const [showUnitDropdowns, setShowUnitDropdowns] = useState({});
  const [reviewDraftMap, setReviewDraftMap] = useState({});
  const [unitSearchTerms, setUnitSearchTerms] = useState({});
  const [dropdownMaxHeights, setDropdownMaxHeights] = useState({});
  const [dropdownAnchors, setDropdownAnchors] = useState({});
  const [creatingIngredient, setCreatingIngredient] = useState(false);
  const [ingredientHistory, setIngredientHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Swipe-to-delete state (like itemmap)
  const prepSwipeRef = useRef({});
  const [prepSwipeOffsets, setPrepSwipeOffsets] = useState({});
  const [prepSwipingIndex, setPrepSwipingIndex] = useState(null);
  const ingredientsListRef = useRef(null);
  const ingredientNameInputRefs = useRef({});
  const ingredientQtyInputRefs = useRef({});

  // State for Step 1 unit dropdown
  const [showMainUnitDropdown, setShowMainUnitDropdown] = useState(false);
  const [mainUnitSearchTerm, setMainUnitSearchTerm] = useState('');
  const mainUnitSelectorRef = useRef(null);
  const [showInlineUnitDropdown, setShowInlineUnitDropdown] = useState(false);
  const [inlineUnitSearchTerm, setInlineUnitSearchTerm] = useState('');

  // Only Step 1 main unit dropdown should relax modal overflow.
  // Keeping Step 0 inline editor in normal scroll mode prevents cards from bleeding outside.
  const allowModalOverflow = showMainUnitDropdown;

  const checkAndConvertKgToG = (unitToCheck) => {
    if (!unitToCheck || !availableUnits || availableUnits.length === 0) return null;
    const symbol = String(unitToCheck.unit_symbol || unitToCheck.unit_name || '').trim().toLowerCase();
    const name = String(unitToCheck.unit_name || '').trim().toLowerCase();
    
    // KG to G
    if (symbol === 'kg' || name === 'kilogram' || name === 'kilograms') {
      const gramUnit = availableUnits.find(u => {
        const s = String(u.unit_symbol || '').trim().toLowerCase();
        const n = String(u.unit_name || '').trim().toLowerCase();
        return s === 'g' || n === 'gram' || n === 'grams';
      });
      if (gramUnit) {
        const currentQty = parseFloat(totalQuantity) || 0;
        return {
          unit: gramUnit,
          convertedQty: currentQty * 1000
        };
      }
    }
    
    // L to ML
    if (symbol === 'l' || name === 'liter' || name === 'liters' || name === 'litre' || name === 'litres') {
      const mlUnit = availableUnits.find(u => {
        const s = String(u.unit_symbol || '').trim().toLowerCase();
        const n = String(u.unit_name || '').trim().toLowerCase();
        return s === 'ml' || n === 'milliliter' || n === 'milliliters' || n === 'millilitre' || n === 'millilitres';
      });
      if (mlUnit) {
        const currentQty = parseFloat(totalQuantity) || 0;
        return {
          unit: mlUnit,
          convertedQty: currentQty * 1000
        };
      }
    }
    
    return null;
  };

  const checkAndConvertIngredientKgToG = (index, unitToCheck) => {
    if (!unitToCheck || !availableUnits || availableUnits.length === 0) return null;
    const symbol = String(unitToCheck.unit_symbol || unitToCheck.unit_name || '').trim().toLowerCase();
    const name = String(unitToCheck.unit_name || '').trim().toLowerCase();
    
    // KG to G
    if (symbol === 'kg' || name === 'kilogram' || name === 'kilograms') {
      const gramUnit = availableUnits.find(u => {
        const s = String(u.unit_symbol || '').trim().toLowerCase();
        const n = String(u.unit_name || '').trim().toLowerCase();
        return s === 'g' || n === 'gram' || n === 'grams';
      });
      if (gramUnit) {
        const currentQty = parseFloat(ingredients[index]?.quantity) || 0;
        return {
          unit: gramUnit,
          convertedQty: currentQty * 1000
        };
      }
    }
    
    // L to ML
    if (symbol === 'l' || name === 'liter' || name === 'liters' || name === 'litre' || name === 'litres') {
      const mlUnit = availableUnits.find(u => {
        const s = String(u.unit_symbol || '').trim().toLowerCase();
        const n = String(u.unit_name || '').trim().toLowerCase();
        return s === 'ml' || n === 'milliliter' || n === 'milliliters' || n === 'millilitre' || n === 'millilitres';
      });
      if (mlUnit) {
        const currentQty = parseFloat(ingredients[index]?.quantity) || 0;
        return {
          unit: mlUnit,
          convertedQty: currentQty * 1000
        };
      }
    }
    
    return null;
  };

  const createRowId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const cloneIngredientList = (list = []) => list.map((item) => ({ ...item }));

  const toTitleCaseWords = (value) => String(value || '')
    .split(' ')
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}` : ''))
    .join(' ');

  const toIngredientDisplayText = (value) => String(value || '')
    .replace(/\s+/g, ' ')
    .toLowerCase();

  const toIngredientNormalized = (value) => toIngredientDisplayText(value).trim();

  const resolvePreparationComponentName = (component, fallbackName = '') => {
    const rawName =
      component?.item_name ||
      component?.ingredient_name ||
      component?.name ||
      component?.ingredient ||
      fallbackName ||
      (component?.atomic_item_id ? `ingredient ${component.atomic_item_id}` : '') ||
      'unnamed ingredient';

    return toIngredientNormalized(rawName);
  };

  const resolvePreparationComponentQuantity = (component) => {
    const rawQuantity = component?.weight ?? component?.quantity ?? component?.qty ?? component?.amount;
    if (rawQuantity === null || rawQuantity === undefined || rawQuantity === '') {
      return '';
    }

    const parsed = Number(rawQuantity);
    return Number.isFinite(parsed) ? String(parsed) : String(rawQuantity);
  };

  const resolvePreparationComponentUnit = (component, fallbackUnit = '') => {
    return component?.unit_name || component?.unit_symbol || component?.unit || fallbackUnit || '';
  };

  const preferredUnitOrder = ['g', 'kg', 'ml', 'l', 'pcs'];

  const getUnitPriority = (unit) => {
    const symbol = String(unit?.unit_symbol || '').trim().toLowerCase();
    const name = String(unit?.unit_name || '').trim().toLowerCase();

    const directIndex = preferredUnitOrder.indexOf(symbol);
    if (directIndex !== -1) return directIndex;

    if (name === 'gram' || name === 'grams') return 0;
    if (name === 'kilogram' || name === 'kilograms') return 1;
    if (name === 'milliliter' || name === 'milliliters' || name === 'millilitre' || name === 'millilitres') return 2;
    if (name === 'liter' || name === 'liters' || name === 'litre' || name === 'litres') return 3;
    if (name === 'piece' || name === 'pieces' || name === 'pcs') return 4;

    return Number.MAX_SAFE_INTEGER;
  };

  const createNewReviewDraftKey = () => `new:${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const reviewDraftStorageKey = `preparationReviewDrafts_${businessId}`;

  const readReviewDraftMap = () => {
    if (typeof window === 'undefined') return {};

    try {
      const raw = window.localStorage.getItem(reviewDraftStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.error('Failed to parse preparation review drafts:', error);
      return {};
    }
  };

  const writeReviewDraftMap = (nextDraftMap) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(reviewDraftStorageKey, JSON.stringify(nextDraftMap));
    setReviewDraftMap(nextDraftMap);
  };

  const getReviewDraftForPreparation = (prepId) => {
    if (!prepId) return null;
    const currentDrafts = readReviewDraftMap();
    return currentDrafts[`prep:${prepId}`] || null;
  };

  const saveReviewDraftByKey = (reviewKey, { prepId = null, isUnsaved = false } = {}) => {
    if (!reviewKey) return null;

    const currentDrafts = readReviewDraftMap();
    currentDrafts[reviewKey] = {
      reviewKey,
      prepId,
      isUnsaved,
      preparationName: toTitleCaseWords(preparationName).trim(),
      totalQuantity,
      selectedUnitId: selectedUnit?.unit_id || null,
      selectedUnitName: selectedUnit?.unit_name || null,
      selectedUnitSymbol: selectedUnit?.unit_symbol || null,
      ingredients: cloneIngredientList(ingredients).map((ingredient) => ({
        atomic_item_id: ingredient.atomic_item_id || null,
        item_name: toIngredientNormalized(ingredient.item_name || ''),
        quantity: ingredient.quantity,
        unit_id: ingredient.unit_id || null,
        unit_name: ingredient.unit_name || null,
        unit_symbol: ingredient.unit_symbol || null,
      })),
      savedAt: Date.now(),
    };

    writeReviewDraftMap(currentDrafts);
    return reviewKey;
  };

  const clearReviewDraftByKey = (reviewKey) => {
    if (!reviewKey) return;

    const currentDrafts = readReviewDraftMap();
    if (!currentDrafts[reviewKey]) return;

    delete currentDrafts[reviewKey];
    writeReviewDraftMap(currentDrafts);
  };

  const clearReviewDraftForPreparation = (prepId) => {
    if (!prepId) return;
    clearReviewDraftByKey(`prep:${prepId}`);
  };

  const clearIngredientInteractionState = () => {
    setSearchTerms({});
    setShowDropdowns({});
    setShowUnitDropdowns({});
    setUnitSearchTerms({});
    setDropdownMaxHeights({});
    setDropdownAnchors({});
    setPrepSwipeOffsets({});
    setPrepSwipingIndex(null);
    prepSwipeRef.current = {};
  };

  const resetIngredientHistory = () => {
    setIngredientHistory([]);
    setHistoryIndex(-1);
  };

  const clearIngredientEditorState = () => {
    clearIngredientInteractionState();
    resetIngredientHistory();
    setIngredients([]);
  };

  const focusIngredientNameInput = (rowKey) => {
    setTimeout(() => {
      const nameInput = ingredientNameInputRefs.current[rowKey];
      if (nameInput) {
        nameInput.focus();
        nameInput.select?.();
      }
    }, 0);
  };

  const focusIngredientQuantityInput = (rowKey) => {
    setTimeout(() => {
      const qtyInput = ingredientQtyInputRefs.current[rowKey];
      if (qtyInput) {
        qtyInput.focus();
        qtyInput.select?.();
      }
    }, 0);
  };

  const pushIngredientHistory = (previousList, newList) => {
    const nextHistory = ingredientHistory.slice(0, historyIndex + 1);
    nextHistory.push({
      previousList: cloneIngredientList(previousList),
      newList: cloneIngredientList(newList)
    });
    setIngredientHistory(nextHistory);
    setHistoryIndex(nextHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex < 0) return;

    const entry = ingredientHistory[historyIndex];
    if (!entry) return;

    clearIngredientInteractionState();
    setIngredients(cloneIngredientList(entry.previousList));
    setHistoryIndex(historyIndex - 1);
  };

  const handleRedo = () => {
    if (historyIndex >= ingredientHistory.length - 1) return;

    const nextIndex = historyIndex + 1;
    const entry = ingredientHistory[nextIndex];
    if (!entry) return;

    clearIngredientInteractionState();
    setIngredients(cloneIngredientList(entry.newList));
    setHistoryIndex(nextIndex);
  };

  const createDropdownAnchor = (placement) => ({
    top: placement.top,
    left: placement.left,
    width: placement.width,
    openUpward: placement.openUpward,
    triggerTop: placement.triggerTop,
    gap: placement.gap,
  });

  const syncDropdownAnchorToRenderedHeight = (dropdownKey, node, maxHeight) => {
    if (!node) return;

    const measuredHeight = Math.min(
      maxHeight || node.scrollHeight || node.offsetHeight || 0,
      node.scrollHeight || node.offsetHeight || 0
    );

    if (!measuredHeight) return;

    setDropdownAnchors((prev) => {
      const current = prev[dropdownKey];
      if (!current?.openUpward || typeof current.triggerTop !== 'number') {
        return prev;
      }

      const gap = typeof current.gap === 'number' ? current.gap : 4;
      const nextTop = Math.max(8, current.triggerTop - measuredHeight - gap);

      if (Math.abs((current.top || 0) - nextTop) < 1) {
        return prev;
      }

      return {
        ...prev,
        [dropdownKey]: {
          ...current,
          top: nextTop,
        }
      };
    });
  };

  const getDropdownPlacement = (triggerElement, preferredHeight = 240, minWidth = 200, align = 'left', options = {}) => {
    if (!triggerElement || typeof window === 'undefined') {
      return { openUpward: false, maxHeight: preferredHeight, top: 0, left: 0, width: minWidth };
    }

    const {
      preferBelow = false,
      minimumVisibleHeight = 96,
    } = options;

    const triggerRect = triggerElement.getBoundingClientRect();
    const modalContainer = triggerElement.closest('.prep-modal-content');
    const bounds = modalContainer
      ? modalContainer.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight };

    const viewportTopPadding = 8;
    const viewportBottomPadding = 8;

    const containerSpaceAbove = triggerRect.top - bounds.top;
    const containerSpaceBelow = bounds.bottom - triggerRect.bottom;
    const viewportSpaceAbove = triggerRect.top - viewportTopPadding;
    const viewportSpaceBelow = window.innerHeight - triggerRect.bottom - viewportBottomPadding;

    const spaceAbove = Math.max(0, Math.min(containerSpaceAbove, viewportSpaceAbove));
    const spaceBelow = Math.max(0, Math.min(containerSpaceBelow, viewportSpaceBelow));

    const requiredSpace = Math.min(preferredHeight, 180);
    const hasEnoughBelow = spaceBelow >= requiredSpace;
    const hasEnoughAbove = spaceAbove >= requiredSpace;

    let openUpward = false;
    if (preferBelow) {
      if (spaceBelow >= minimumVisibleHeight) {
        openUpward = false;
      } else if (hasEnoughAbove && spaceAbove > spaceBelow) {
        openUpward = true;
      } else {
        openUpward = spaceAbove > spaceBelow && spaceBelow < 40;
      }
    } else if (hasEnoughBelow) {
      openUpward = false;
    } else if (hasEnoughAbove) {
      openUpward = true;
    } else {
      openUpward = spaceAbove > spaceBelow;
    }

    const availableSpace = openUpward ? spaceAbove : spaceBelow;
    const minimumHeight = preferBelow ? 80 : 120;
    const maxHeight = Math.max(minimumHeight, Math.min(preferredHeight, Math.floor(availableSpace - 8)));

    const width = Math.max(minWidth, Math.round(triggerRect.width));
    const leftCandidate = align === 'right' ? triggerRect.right - width : triggerRect.left;
    const left = Math.max(8, Math.min(leftCandidate, window.innerWidth - width - 8));
    const gap = 4;
    const top = openUpward
      ? Math.max(8, triggerRect.top - maxHeight - gap)
      : Math.min(window.innerHeight - maxHeight - 8, triggerRect.bottom + gap);

    return {
      openUpward,
      maxHeight,
      top,
      left,
      width,
      triggerTop: triggerRect.top,
      gap,
    };
  };

  // Load existing preparations
  const loadExistingPreparations = async () => {
    try {
      setLoadingPreparations(true);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
      const response = await fetch(`${apiUrl}/medium-features/decomposition/templates?businessId=${businessId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      
      if (response.ok) {
        const result = await response.json();
        setExistingPreparations(result.personalized || []);
        setReviewDraftMap(readReviewDraftMap());
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ API Error:', response.status, errorData);
      }
    } catch (error) {
      console.error('Failed to load preparations:', error);
    } finally {
      setLoadingPreparations(false);
    }
  };

  // Load available ingredients
  const loadAvailableIngredients = async () => {
    try {
      const data = await InventoryService.getInventoryItems(businessId, 2000);
      const ingredients = data?.data || [];
      setAvailableIngredients(ingredients);
    } catch (error) {
      console.error('Failed to load ingredients:', error);
    }
  };

  const loadRecipes = async () => {
    try {
      const data = await RecipesService.getRecipes(businessId);
      setRecipes(data || []);
    } catch (error) {
      console.error('Failed to load recipes:', error);
      setRecipes([]);
    }
  };

  // Load available units
  const loadAvailableUnits = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
      const response = await fetch(`${apiUrl}/unit-mapping/units`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        // The API returns { success: true, data: { kitchen, supplier, container } }
        // We want kitchen units for preparations
        const units = result.data?.kitchen || [];
        setAvailableUnits(units);
        // Set default unit to first one
        if (units && units.length > 0) {
          setSelectedUnit(units[0]);
        }
      } else {
        console.error('❌ Failed to load units:', response.status);
        setAvailableUnits([]);
      }
    } catch (error) {
      console.error('Failed to load units:', error);
      setAvailableUnits([]);
    }
  };

  useEffect(() => {
    loadExistingPreparations();
    loadAvailableIngredients();
    loadAvailableUnits();
    loadRecipes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    setReviewDraftMap(readReviewDraftMap());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isInsideDropdown = e.target.closest(
        '.prep-dropdown-container, .prep-main-unit-dropdown, .pcm-suggestion-dropdown, .pcm-unit-dropdown, .pcm-unit-wrapper, .pcdm-inline-unit-wrap, .pcdm-inline-unit-dropdown'
      );
      if (!isInsideDropdown) {
        setShowDropdowns({});
        setShowUnitDropdowns({});
        setDropdownMaxHeights({});
        setDropdownAnchors({});
        setShowMainUnitDropdown(false);
        setShowInlineUnitDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleViewportResize = () => {
      setShowDropdowns({});
      setShowUnitDropdowns({});
      setDropdownMaxHeights({});
      setDropdownAnchors({});
    };

    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleNext = () => {
    if (!preparationName.trim()) {
      openFeedbackAlert('Preparation Name Required', 'Please enter preparation name.');
      return;
    }
    if (!totalQuantity || parseFloat(totalQuantity) <= 0) {
      openFeedbackAlert('Valid Yield Required', 'Please enter a valid quantity.');
      return;
    }
    if (!selectedUnit) {
      openFeedbackAlert('Unit Required', 'Please select a unit.');
      return;
    }
    
    // Check for duplicate name against existing preparations (case-insensitive)
    const normalizedName = preparationName.trim().toLowerCase();
    const duplicateInSaved = existingPreparations.some(prep => {
      if (editingPreparationId && prep.id === editingPreparationId) return false;
      if (activeReviewDraftKey && prep.draft_key === activeReviewDraftKey) return false;
      const nameToCheck = prep.composite_name || prep.name || '';
      return nameToCheck.trim().toLowerCase() === normalizedName;
    });

    const duplicateInDrafts = Object.entries(reviewDraftMap || {}).some(([key, draft]) => {
      if (activeReviewDraftKey && key === activeReviewDraftKey) return false;
      if (!draft?.isUnsaved) return false;
      const nameToCheck = draft.preparationName || '';
      return nameToCheck.trim().toLowerCase() === normalizedName;
    });

    if (duplicateInSaved || duplicateInDrafts) {
      setDuplicateError({ name: preparationName.trim() });
      return;
    }

    setStep(2);
    if (ingredients.length === 0) {
      setTimeout(() => {
        addIngredientToList([]);
      }, 50);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      if (editingPreparationId) {
        setInlineEditingPreparationId(null);
        setStep(0);
        setEditingPreparationId(null);
        setActiveReviewDraftKey(null);
        setPreparationName('');
        setTotalQuantity('');
        clearIngredientEditorState();
      } else {
        clearIngredientInteractionState();
        setStep(1);
      }
    } else if (step === 1) {
      // Reset to list view
      setInlineEditingPreparationId(null);
      setStep(0);
      setEditingPreparationId(null);
      setActiveReviewDraftKey(null);
      setPreparationName('');
      setTotalQuantity('');
      clearIngredientEditorState();
    }
  };

  const handleCreateNew = () => {
    setInlineEditingPreparationId(null);
    setEditingPreparationId(null);
    setActiveReviewDraftKey(null);
    setPreparationName('');
    setTotalQuantity('');
    clearIngredientEditorState();
    setStep(1);
  };

  const handleEditPreparation = (prep, targetStep = 2) => {
    const localDraftKey = prep?.draft_key || (
      typeof prep?.id === 'string' && String(prep.id).startsWith('new:')
        ? String(prep.id)
        : null
    );
    const isLocalReviewDraft = Boolean(prep?.is_local_review_draft || localDraftKey);

    if (isLocalReviewDraft && localDraftKey) {
      const reviewDraft = readReviewDraftMap()[localDraftKey];
      if (!reviewDraft) {
        setReviewDraftMap(readReviewDraftMap());
        openFeedbackAlert('Draft Not Found', 'This local review draft is no longer available.');
        return;
      }

      const resolvedUnits = availableUnits || [];
      const normalizeUnitValue = (value) => (value || '').trim().toLowerCase();

      setEditingPreparationId(null);
      setActiveReviewDraftKey(localDraftKey);
      setPreparationName(toTitleCaseWords(reviewDraft.preparationName || prep.composite_name || ''));
      setTotalQuantity(reviewDraft.totalQuantity !== undefined && reviewDraft.totalQuantity !== null ? String(reviewDraft.totalQuantity) : '');

      const restoredUnit = resolvedUnits.find((unit) => (
        Number(unit.unit_id) === Number(reviewDraft.selectedUnitId) ||
        unit.unit_name === reviewDraft.selectedUnitName ||
        unit.unit_symbol === reviewDraft.selectedUnitSymbol
      ));

      if (restoredUnit) {
        setSelectedUnit(restoredUnit);
      }

      const restoredIngredients = (reviewDraft.ingredients || []).map((ingredient) => {
        const componentUnitValue = normalizeUnitValue(ingredient.unit_name || ingredient.unit_symbol);
        const matchingUnit = resolvedUnits.find((unit) => (
          normalizeUnitValue(unit.unit_name) === componentUnitValue ||
          normalizeUnitValue(unit.unit_symbol) === componentUnitValue ||
          Number(unit.unit_id) === Number(ingredient.unit_id)
        ));
        const fallbackItem = availableIngredients.find((item) => (
          Number(item.item_id) === Number(ingredient.atomic_item_id)
        ));
        const resolvedName = toIngredientNormalized(ingredient.item_name || fallbackItem?.name || '');
        const fallbackUnit = fallbackItem?.unit_symbol || fallbackItem?.unit_name || '';

        // Auto-scale to the app-wide readable unit (0.01 kg -> 10 g, 1 kg stays kg).
        const rawUnitSym = matchingUnit?.unit_symbol || ingredient.unit_symbol || ingredient.unit_name || fallbackUnit;
        const scaled = autoScaleToSymbol(ingredient.quantity, rawUnitSym);
        const scaledUnit = resolvedUnits.find((u) => (
          normalizeUnitValue(u.unit_symbol) === normalizeUnitValue(scaled.unit) ||
          normalizeUnitValue(u.unit_name) === normalizeUnitValue(scaled.unit)
        )) || matchingUnit || null;

        return {
          rowId: createRowId(),
          atomic_item_id: ingredient.atomic_item_id,
          item_name: resolvedName,
          quantity: scaled.qty,
          unit_id: scaledUnit?.unit_id || matchingUnit?.unit_id || ingredient.unit_id || null,
          unit_name: scaledUnit?.unit_name || ingredient.unit_name || fallbackUnit,
          unit_symbol: scaledUnit?.unit_symbol || scaled.unit || ingredient.unit_symbol || ingredient.unit_name || fallbackUnit,
        };
      });

      clearIngredientInteractionState();
      resetIngredientHistory();
      setIngredients(restoredIngredients);
      setUnitWarningItems([]);
      setShowUnitWarning(false);
      setStep(targetStep);
      if (targetStep === 2 && restoredIngredients.length === 0) {
        setTimeout(() => {
          addIngredientToList(restoredIngredients);
        }, 50);
      }
      return;
    }

    setEditingPreparationId(prep.id);
    setActiveReviewDraftKey(null);
    setPreparationName(toTitleCaseWords(prep.composite_name));
    
    // Use the stored total_weight and unit from database
    const totalWeightValue = parseFloat(prep.total_weight || 0);
    const storedUnitName = prep.total_weight_unit_name;
    
    // If unit info is available, use it directly
    if (storedUnitName && availableUnits) {
      setTotalQuantity(totalWeightValue.toString());
      const unit = availableUnits.find(u => u.unit_name === storedUnitName);
      if (unit) {
        setSelectedUnit(unit);
      } else {
        // Fallback to Gram if unit not found
        const gramUnit = availableUnits.find(u => u.unit_name === 'Gram');
        if (gramUnit) setSelectedUnit(gramUnit);
      }
    } else {
      // Fallback: Old logic for backward compatibility (assume value is in grams)
      let displayValue = totalWeightValue;
      let displayUnit = 'Gram';
      
      if (totalWeightValue >= 1000) {
        displayValue = totalWeightValue / 1000;
        displayUnit = 'Kilogram';
      }
      
      setTotalQuantity(displayValue.toString());
      
      if (availableUnits) {
        const unit = availableUnits.find(u => u.unit_name === displayUnit);
        if (unit) setSelectedUnit(unit);
      }
    }
    
    // Load components
    const resolvedUnits = availableUnits || [];
    const canResolveUnits = resolvedUnits.length > 0;
    const normalizeUnitValue = (value) => (value || '').trim().toLowerCase();
    const unmatchedUnits = [];

    // Auto-scale a (quantity, unitSymbol) to the app-wide readable unit (0.01 kg -> 10 g,
    // 1 kg stays kg), then resolve the matching availableUnit for the dropdown.
    const applyAutoScale = (rawQty, rawUnitSym, matchingUnit) => {
      const scaled = autoScaleToSymbol(rawQty, rawUnitSym);
      const scaledUnit = resolvedUnits.find((u) => (
        normalizeUnitValue(u.unit_symbol) === normalizeUnitValue(scaled.unit) ||
        normalizeUnitValue(u.unit_name) === normalizeUnitValue(scaled.unit)
      )) || matchingUnit || null;
      return {
        quantity: scaled.qty,
        unit_id: scaledUnit?.unit_id || matchingUnit?.unit_id || null,
        unit_name: scaledUnit?.unit_name || scaled.unit,
        unit_symbol: scaledUnit?.unit_symbol || scaled.unit,
      };
    };

    const loadedIngredients = (prep.components || []).map(c => {
      const componentUnitLabel = resolvePreparationComponentUnit(c);
      const componentUnitValue = normalizeUnitValue(componentUnitLabel);
      const matchingUnit = resolvedUnits.find(u => (
        normalizeUnitValue(u.unit_name) === componentUnitValue ||
        normalizeUnitValue(u.unit_symbol) === componentUnitValue
      ));
      const fallbackItem = availableIngredients.find((item) => (
        Number(item.item_id) === Number(c.atomic_item_id)
      ));
      const resolvedName = resolvePreparationComponentName(c, fallbackItem?.name || '');
      const fallbackUnit = fallbackItem?.unit_symbol || fallbackItem?.unit_name || '';

      if (!matchingUnit && canResolveUnits && componentUnitLabel) {
        unmatchedUnits.push({
          item_name: resolvedName,
          unit_name: componentUnitLabel
        });
      }

      const rawUnitSym = matchingUnit?.unit_symbol || componentUnitLabel || fallbackUnit;
      const scaled = applyAutoScale(resolvePreparationComponentQuantity(c), rawUnitSym, matchingUnit);

      return {
        rowId: createRowId(),
        atomic_item_id: c.atomic_item_id,
        item_name: resolvedName,
        quantity: scaled.quantity,
        unit_id: scaled.unit_id,
        unit_name: scaled.unit_name || componentUnitLabel || fallbackUnit,
        unit_symbol: scaled.unit_symbol || componentUnitLabel || fallbackUnit
      };
    });

    const reviewDraft = getReviewDraftForPreparation(prep.id);
    const hasDraftIngredients = Array.isArray(reviewDraft?.ingredients) && reviewDraft.ingredients.length > 0;

    const restoredIngredients = hasDraftIngredients
      ? reviewDraft.ingredients.map((ingredient) => {
          const componentUnitValue = normalizeUnitValue(ingredient.unit_name || ingredient.unit_symbol);
          const matchingUnit = resolvedUnits.find((unit) => (
            normalizeUnitValue(unit.unit_name) === componentUnitValue ||
            normalizeUnitValue(unit.unit_symbol) === componentUnitValue ||
            Number(unit.unit_id) === Number(ingredient.unit_id)
          ));

          const fallbackItem = availableIngredients.find((item) => (
            Number(item.item_id) === Number(ingredient.atomic_item_id)
          ));
          const resolvedName = toIngredientNormalized(ingredient.item_name || fallbackItem?.name || '');
          const fallbackUnit = fallbackItem?.unit_symbol || fallbackItem?.unit_name || '';

          const rawUnitSym = matchingUnit?.unit_symbol || ingredient.unit_symbol || ingredient.unit_name || fallbackUnit;
          const scaled = applyAutoScale(ingredient.quantity, rawUnitSym, matchingUnit);

          return {
            rowId: createRowId(),
            atomic_item_id: ingredient.atomic_item_id,
            item_name: resolvedName,
            quantity: scaled.quantity,
            unit_id: scaled.unit_id || ingredient.unit_id || null,
            unit_name: scaled.unit_name || ingredient.unit_name || fallbackUnit,
            unit_symbol: scaled.unit_symbol || ingredient.unit_symbol || ingredient.unit_name || fallbackUnit,
          };
        })
      : loadedIngredients;

    clearIngredientInteractionState();
    resetIngredientHistory();
    setIngredients(restoredIngredients);

    if (reviewDraft) {
      if (reviewDraft.preparationName) {
        setPreparationName(toTitleCaseWords(reviewDraft.preparationName));
      }

      if (reviewDraft.totalQuantity !== undefined && reviewDraft.totalQuantity !== null) {
        setTotalQuantity(String(reviewDraft.totalQuantity));
      }

      const restoredUnit = resolvedUnits.find((unit) => (
        Number(unit.unit_id) === Number(reviewDraft.selectedUnitId) ||
        unit.unit_name === reviewDraft.selectedUnitName ||
        unit.unit_symbol === reviewDraft.selectedUnitSymbol
      ));

      if (restoredUnit) {
        setSelectedUnit(restoredUnit);
      }
    }

    // Show warning popup if any units couldn't be resolved
    if (!reviewDraft && unmatchedUnits.length > 0) {
      setUnitWarningItems(unmatchedUnits);
      setShowUnitWarning(true);
    } else {
      setUnitWarningItems([]);
      setShowUnitWarning(false);
    }
    
    setStep(targetStep);
    if (targetStep === 2 && restoredIngredients.length === 0) {
      setTimeout(() => {
        addIngredientToList(restoredIngredients);
      }, 50);
    }
  };

  const handleMarkAsReview = () => {
    const reviewKey = editingPreparationId
      ? `prep:${editingPreparationId}`
      : (activeReviewDraftKey || createNewReviewDraftKey());

    saveReviewDraftByKey(reviewKey, {
      prepId: editingPreparationId || null,
      isUnsaved: !editingPreparationId,
    });

    setInlineEditingPreparationId(null);
    setEditingPreparationId(null);
    setActiveReviewDraftKey(null);
    setPreparationName('');
    setTotalQuantity('');
    clearIngredientEditorState();
    setStep(0);

    openFeedbackAlert('Marked as Review', 'Preparation changes are saved locally. Open this preparation again to continue.');
  };

  const handleOpenPreparationEditor = (e, prep) => {
    e.stopPropagation();
    if (inlineEditingPreparationId === prep.id) {
      setInlineEditingPreparationId(null);
      setEditingPreparationId(null);
      setActiveReviewDraftKey(null);
      setPreparationName('');
      setTotalQuantity('');
      clearIngredientEditorState();
      setInlineUnitSearchTerm('');
      setShowInlineUnitDropdown(false);
      return;
    }

    setInlineEditingPreparationId(prep.id);
    setInlineUnitSearchTerm('');
    setShowInlineUnitDropdown(false);
    handleEditPreparation(prep, 0);
  };

  const handleOpenInlineDelete = (e, prep) => {
    e.stopPropagation();
    setPreparationToDelete(prep);
    setShowDeletePreparationModal(true);
  };

  const addIngredientToList = (baseList) => {
    clearIngredientInteractionState();

    const newIngredient = {
      rowId: createRowId(),
      atomic_item_id: null,
      item_name: '',
      quantity: '',
      unit_id: availableUnits && availableUnits.length > 0 ? availableUnits[0].unit_id : null,
      unit_name: availableUnits && availableUnits.length > 0 ? availableUnits[0].unit_name : 'g',
      unit_symbol: availableUnits && availableUnits.length > 0 ? availableUnits[0].unit_symbol : 'g'
    };

    const previousList = cloneIngredientList(baseList);
    const nextIngredients = [...previousList, newIngredient];
    setIngredients(nextIngredients);
    pushIngredientHistory(previousList, nextIngredients);

    setTimeout(() => {
      if (ingredientsListRef.current) {
        ingredientsListRef.current.scrollTop = ingredientsListRef.current.scrollHeight;
      }
    }, 0);

    focusIngredientNameInput(newIngredient.rowId);
  };

  const addIngredient = () => {
    addIngredientToList(ingredients);
  };

  const updateIngredient = (index, field, value) => {
    const previousList = cloneIngredientList(ingredients);
    const updated = cloneIngredientList(ingredients);
    if (!updated[index]) return;
    const normalizedValue = (field === 'item_name' && typeof value === 'string')
      ? toIngredientDisplayText(value)
      : value;
    if (updated[index][field] === normalizedValue) return;
    updated[index][field] = normalizedValue;
    
    // Update item details when item selected
    if (field === 'atomic_item_id') {
      const item = availableIngredients.find(i => i.item_id === parseInt(value));
      if (item) {
        updated[index].item_name = toIngredientNormalized(item.name);
        updated[index].unit_id = item.standard_unit_id;
        updated[index].unit_name = item.unit_symbol || 'g';
      }
    }
    
    setIngredients(updated);
    pushIngredientHistory(previousList, updated);
  };

  const removeIngredient = (rowId, index) => {
    const previousList = cloneIngredientList(ingredients);
    const rowKey = rowId || String(index);
    const nextIngredients = rowId
      ? previousList.filter(item => item.rowId !== rowId)
      : previousList.filter((_, i) => i !== index);
    if (rowId) {
      setIngredients(nextIngredients);
    } else {
      setIngredients(nextIngredients);
    }
    pushIngredientHistory(previousList, nextIngredients);
    setSearchTerms(prev => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setShowDropdowns(prev => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setShowUnitDropdowns(prev => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setUnitSearchTerms(prev => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setDropdownMaxHeights(prev => {
      const next = { ...prev };
      delete next[`search-${rowKey}`];
      delete next[`unit-${rowKey}`];
      return next;
    });
    setDropdownAnchors(prev => {
      const next = { ...prev };
      delete next[`search-${rowKey}`];
      delete next[`unit-${rowKey}`];
      return next;
    });
  };

  const handleDeleteAllIngredients = () => {
    if (ingredients.length === 0) return;

    openFeedbackConfirm({
      title: 'Delete All Ingredients',
      message: `Delete all ${ingredients.length} ingredient(s)? You can undo this action.`,
      confirmLabel: 'Delete All',
      cancelLabel: 'Keep Ingredients',
      onConfirm: () => {
        const previousList = cloneIngredientList(ingredients);
        clearIngredientInteractionState();
        setIngredients([]);
        pushIngredientHistory(previousList, []);
      }
    });
  };

  // ─── Swipe-to-delete handlers (like itemmap) ───
  const handlePrepTouchStart = (index, event) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    const target = event.target;
    const isControl = target.closest(
      'button, [role="button"], .pcm-controls, .pcm-suggestion-dropdown, .pcm-unit-dropdown'
    );
    if (isControl) return;
    const touch = event.touches[0];
    setPrepSwipingIndex(index);
    prepSwipeRef.current = {
      index,
      startX: touch.clientX,
      startY: touch.clientY,
      endX: touch.clientX,
      endY: touch.clientY
    };
  };

  const handlePrepTouchMove = (index, event) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    if (prepSwipeRef.current.index !== index) return;
    const touch = event.touches[0];
    prepSwipeRef.current.endX = touch.clientX;
    prepSwipeRef.current.endY = touch.clientY;
    const deltaX = touch.clientX - prepSwipeRef.current.startX;
    const bounded = Math.max(-120, Math.min(120, deltaX));
    setPrepSwipeOffsets(prev => ({ ...prev, [index]: bounded }));
  };

  const handlePrepTouchEnd = (index) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    const swipe = prepSwipeRef.current;
    if (swipe.index !== index) return;
    const deltaX = swipe.endX - swipe.startX;
    const deltaY = swipe.endY - swipe.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const shouldDelete = absX > 60 && absX > absY * 1.5;
    if (shouldDelete) {
      const exitOffset = deltaX > 0 ? 160 : -160;
      setPrepSwipeOffsets(prev => ({ ...prev, [index]: exitOffset }));
      setTimeout(() => {
        const ing = ingredients[index];
        removeIngredient(ing?.rowId, index);
        setPrepSwipeOffsets(prev => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }, 120);
    } else {
      setPrepSwipeOffsets(prev => ({ ...prev, [index]: 0 }));
    }
    setPrepSwipingIndex(null);
    prepSwipeRef.current = {};
  };

  const createNewIngredient = async (ingredientName, index) => {
    try {
      setCreatingIngredient(true);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
      const normalizedIngredientName = toIngredientNormalized(ingredientName);
      
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // 🔍 FRONTEND DEDUP CHECK: Prevent adding ingredient that already exists in list
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      const existingInList = ingredients.find((ing, i) => 
        i !== index && (ing.item_name || '').trim().toLowerCase() === normalizedIngredientName.trim().toLowerCase()
      );
      if (existingInList) {
        openFeedbackAlert(
          'Duplicate Ingredient',
          `"${normalizedIngredientName}" is already in this preparation. Please update the existing entry instead.`
        );
        setCreatingIngredient(false);
        return;
      }

      // Get the selected unit for this ingredient row
      const currentIngredient = ingredients[index];
      const unitToUse = currentIngredient.unit_symbol || 'g';
      const rowKey = currentIngredient?.rowId || String(index);
      
      const response = await fetch(`${apiUrl}/inventory/items`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          business_id: businessId,
          name: normalizedIngredientName,
          unit_symbol: unitToUse
        })
      });

      if (response.ok) {
        const result = await response.json();
        await loadAvailableIngredients();
        
        // ✅ Backend returns existing item_id if duplicate found (case-insensitive)
        const resolvedItemId = result.data?.item_id || result.data?.id || result.item?.item_id;
        const resolvedName = result.data?.name || normalizedIngredientName;

        if (result.existing) {
          console.log(`🔁 [PREP DEDUP] "${normalizedIngredientName}" resolved to existing "${resolvedName}" (id: ${resolvedItemId})`);
        }
        
        // Auto-select the resolved ingredient
        const previousList = cloneIngredientList(ingredients);
        const updated = cloneIngredientList(ingredients);
        updated[index].atomic_item_id = resolvedItemId;
        updated[index].item_name = toTitleCaseWords(resolvedName);
        setIngredients(updated);
        pushIngredientHistory(previousList, updated);
        setSearchTerms({ ...searchTerms, [rowKey]: toTitleCaseWords(resolvedName) });
        setShowDropdowns({});
        setDropdownMaxHeights(prev => {
          const next = { ...prev };
          delete next[`search-${rowKey}`];
          return next;
        });
        setDropdownAnchors(prev => {
          const next = { ...prev };
          delete next[`search-${rowKey}`];
          return next;
        });
        focusIngredientQuantityInput(rowKey);
      }
    } catch (error) {
      console.error('Failed to create ingredient:', error);
      openFeedbackAlert('Failed to Create Ingredient', 'Failed to create ingredient.');
    } finally {
      setCreatingIngredient(false);
    }
  };

  const importIngredientsFromMenu = async (menuItem) => {
    if (!menuItem) return;
    // Close suggestion dropdown immediately so it does not overlap the confirm dialog.
    setShowDropdowns({});
    setDropdownMaxHeights({});
    setDropdownAnchors({});
    openFeedbackConfirm({
      title: 'Import Ingredients',
      message: `Import all ingredients from "${toTitleCaseWords(menuItem.name)}"? This will replace current ingredients.`,
      confirmLabel: 'Import',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          setImportingMenu(true);
          const menuItemId = menuItem.menu_item_id || menuItem.recipe_id || menuItem.id;
          const imported = await RecipesService.getRecipeIngredients(menuItemId, businessId);
          if (!imported || imported.length === 0) {
            openFeedbackAlert('No Ingredients Found', 'No ingredients found in the selected item.');
            return;
          }
          const mapped = imported.map((ing) => {
            const name = ing.ingredient_name || ing.name || ing.item_name || '';
            const qty = ing.quantity ?? ing.weight ?? '';
            const unitSym = ing.unit_symbol || ing.unit_name || ing.unit || 'g';
            const matchUnit = (availableUnits || []).find(
              u => u.unit_symbol === unitSym || u.unit_name === unitSym
            );
            return {
              rowId: createRowId(),
              atomic_item_id: ing.inventory_id || ing.atomic_item_id || ing.item_id || null,
              item_name: toIngredientNormalized(name),
              quantity: qty !== '' ? String(qty) : '',
              unit_id: matchUnit?.unit_id || null,
              unit_name: matchUnit?.unit_name || unitSym,
              unit_symbol: matchUnit?.unit_symbol || unitSym
            };
          });
          const previousList = cloneIngredientList(ingredients);
          const nextIngredients = mapped;
          setIngredients(nextIngredients);
          pushIngredientHistory(previousList, nextIngredients);
          setShowDropdowns({});
        } catch (error) {
          console.error('Failed to import ingredients:', error);
          openFeedbackAlert('Failed to Import Ingredients', 'Failed to import ingredients.');
        } finally {
          setImportingMenu(false);
        }
      }
    });
  };

  const importIngredientsFromPreparation = async (prep, triggerRowId = null, triggerIndex = null) => {
    if (!prep) return;
    // Close suggestion dropdown immediately so it does not overlap the confirm dialog.
    setShowDropdowns({});
    setDropdownMaxHeights({});
    setDropdownAnchors({});
    openFeedbackConfirm({
      title: 'Import Saved Preparation',
      message: `Add ingredients from saved preparation "${toTitleCaseWords(prep.composite_name)}"? New ingredients will be added to the current list; any that are already present will be skipped.`,
      confirmLabel: 'Import',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          setImportingPreparation(true);
          const components = Array.isArray(prep.components) ? prep.components : [];
          if (components.length === 0) {
            openFeedbackAlert('No Ingredients Found', 'No ingredients found in the selected preparation.');
            return;
          }

          const mapped = components.map((component) => {
            const componentUnit = resolvePreparationComponentUnit(component, 'g');
            const matchingUnit = (availableUnits || []).find(
              (unit) => unit.unit_name === componentUnit || unit.unit_symbol === componentUnit
            );

            return {
              rowId: createRowId(),
              atomic_item_id: component.atomic_item_id || component.item_id || null,
              item_name: resolvePreparationComponentName(component),
              quantity: resolvePreparationComponentQuantity(component),
              unit_id: matchingUnit?.unit_id || null,
              unit_name: matchingUnit?.unit_name || componentUnit,
              unit_symbol: matchingUnit?.unit_symbol || componentUnit
            };
          });

          // Add imported ingredients to the current list instead of replacing it.
          // Skip any whose name already exists (in the current list or earlier in
          // this same import) — case-insensitive, matching the save-time dedup check.
          const normalizeName = (value) => String(value || '').trim().toLowerCase();
          const historyBefore = cloneIngredientList(ingredients);

          // The row the user typed the preparation name into (e.g. "batt" to find
          // "Batter") is just a search box, not a real ingredient. Drop it before
          // appending — same as the itemmap import — but only while it still holds
          // no real quantity, so a row the user actually filled in is never lost.
          const isTriggerRow = (ing, idx) =>
            (triggerRowId != null && ing.rowId === triggerRowId) ||
            (triggerRowId == null && triggerIndex != null && idx === triggerIndex);
          const baseList = historyBefore.filter(
            (ing, idx) => !isTriggerRow(ing, idx) || Number(ing.quantity) > 0
          );
          const triggerRemoved = baseList.length !== historyBefore.length;

          const existingNames = new Set(
            baseList.map((ing) => normalizeName(ing.item_name)).filter(Boolean)
          );

          const addedRows = [];
          const skippedNames = [];
          mapped.forEach((row) => {
            const key = normalizeName(row.item_name);
            if (!key) return;
            if (existingNames.has(key)) {
              skippedNames.push(row.item_name);
              return;
            }
            existingNames.add(key);
            addedRows.push(row);
          });

          // Nothing new AND the search row wasn't a throwaway placeholder → leave
          // everything untouched (no list change, no history entry).
          if (addedRows.length === 0 && !triggerRemoved) {
            openFeedbackAlert(
              'Nothing to Import',
              skippedNames.length > 0
                ? `All ingredients from "${toTitleCaseWords(prep.composite_name)}" are already in the list.`
                : 'No ingredients found in the selected preparation.'
            );
            return;
          }

          const nextIngredients = [...baseList, ...addedRows];
          setIngredients(nextIngredients);
          pushIngredientHistory(historyBefore, nextIngredients);

          // Forget the search text of the row we just consumed so it can't reappear.
          if (triggerRemoved) {
            const triggerRowKey = triggerRowId != null ? triggerRowId : String(triggerIndex);
            setSearchTerms((prev) => {
              if (!(triggerRowKey in prev)) return prev;
              const next = { ...prev };
              delete next[triggerRowKey];
              return next;
            });
          }
          setShowDropdowns({});

          // Tell the user only when something was skipped or nothing new was added;
          // a clean add stays silent, like the menu-item import.
          if (addedRows.length === 0) {
            openFeedbackAlert(
              'Already in List',
              `All ingredients from "${toTitleCaseWords(prep.composite_name)}" are already in the list.`
            );
          } else if (skippedNames.length > 0) {
            const skippedLabels = [...new Set(skippedNames.map(toTitleCaseWords))];
            openFeedbackAlert(
              'Ingredients Added',
              `Added ${addedRows.length} ingredient${addedRows.length === 1 ? '' : 's'}. ` +
              `Skipped ${skippedLabels.length} already in the list: ${skippedLabels.join(', ')}.`
            );
          }
        } catch (error) {
          console.error('Failed to import preparation ingredients:', error);
          openFeedbackAlert('Failed to Import Ingredients', 'Failed to import ingredients from saved preparation.');
        } finally {
          setImportingPreparation(false);
        }
      }
    });
  };

  const handleSave = async (customIngredientsList) => {
    const listToSave = Array.isArray(customIngredientsList) ? customIngredientsList : ingredients;
    // Validation
    if (listToSave.length === 0) {
      openFeedbackAlert('Ingredients Required', 'Please add at least one ingredient.');
      return;
    }

    // Check for missing names or quantities
    const invalidIngredients = listToSave.filter(i => !i.item_name?.trim() || !i.quantity || parseFloat(i.quantity) <= 0);
    if (invalidIngredients.length > 0) {
      openFeedbackAlert('Incomplete Ingredient Details', 'Please fill in all ingredient details with valid quantities.');
      return;
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 🔍 DUPLICACY CHECK: Prevent duplicate ingredients within this preparation
    // (Same logic as itemmap — case-insensitive name comparison)
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    const seenNames = new Map();
    const duplicateNames = [];
    for (const ing of listToSave) {
      const key = (ing.item_name || '').trim().toLowerCase();
      if (!key) continue;
      if (seenNames.has(key)) {
        duplicateNames.push(toTitleCaseWords(ing.item_name));
      } else {
        seenNames.set(key, true);
      }
    }
    if (duplicateNames.length > 0) {
      openFeedbackAlert(
        'Duplicate Ingredients Found',
        `The following ingredient(s) appear more than once: ${[...new Set(duplicateNames)].join(', ')}. Please remove duplicates before saving.`
      );
      return;
    }

    // Check for unresolved units before saving
    const missingUnits = listToSave.filter(i => !i.unit_id && i.unit_name);
    if (missingUnits.length > 0) {
      setUnitWarningItems(missingUnits.map(i => ({ item_name: toIngredientNormalized(i.item_name), unit_name: i.unit_name })));
      setShowUnitWarning(true);
      return;
    }

    try {
      setSaving(true);
      
      // Auto-create ingredients that don't have atomic_item_id
      const updatedIngredients = [];
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
      
      for (const ingredient of listToSave) {
        if (!ingredient.atomic_item_id) {
          const normalizedName = toIngredientNormalized(ingredient.item_name);
          const matchedItem = availableIngredients.find((item) => (
            toIngredientNormalized(item?.name) === normalizedName
          ));

          if (matchedItem) {
            updatedIngredients.push({
              ...ingredient,
              atomic_item_id: matchedItem.item_id || matchedItem.id,
              item_name: toTitleCaseWords(matchedItem.name),
              unit_id: ingredient.unit_id || matchedItem.standard_unit_id || null,
              unit_name: ingredient.unit_name || matchedItem.unit_symbol || matchedItem.standardUnit || ingredient.unit_name,
              unit_symbol: ingredient.unit_symbol || matchedItem.unit_symbol || matchedItem.standardUnit || ingredient.unit_symbol
            });
            continue;
          }

          // Create new ingredient (backend now handles case-insensitive dedup)
          try {
            const response = await fetch(`${apiUrl}/inventory/items`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                business_id: businessId,
                name: ingredient.item_name.trim(),
                unit_symbol: ingredient.unit_symbol || ingredient.unit_name || 'g'
              })
            });

            if (response.ok) {
              const result = await response.json();
              // ✅ Backend returns existing item_id if duplicate found (case-insensitive)
              const resolvedItemId = result.data?.item_id || result.data?.id || result.item?.item_id;
              const resolvedName = result.data?.name || ingredient.item_name.trim();
              
              if (result.existing) {
                console.log(`🔁 [PREP DEDUP] "${ingredient.item_name}" resolved to existing item "${resolvedName}" (id: ${resolvedItemId})`);
              }
              
              updatedIngredients.push({
                ...ingredient,
                atomic_item_id: resolvedItemId,
                item_name: toTitleCaseWords(resolvedName)
              });
            } else {
              throw new Error(`Failed to create ingredient: ${ingredient.item_name}`);
            }
          } catch (error) {
            console.error('Failed to create ingredient:', error);
            throw new Error(`Could not create ingredient "${ingredient.item_name}"`);
          }
        } else {
          updatedIngredients.push(ingredient);
        }
      }

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // 🛡️ FINAL GUARD: Ensure every ingredient resolved to a valid inventory item
      // This prevents "null value in column atomic_item_id" DB constraint errors
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      const unresolvedIngredients = updatedIngredients.filter(i => !i.atomic_item_id);
      if (unresolvedIngredients.length > 0) {
        const unresolvedNames = unresolvedIngredients
          .map(i => `"${i.item_name || 'Unknown'}"`);
        throw new Error(
          `Could not link ${unresolvedNames.join(', ')} to inventory. ` +
          `Please select each ingredient from the dropdown list or type a valid name.`
        );
      }
      
      const getUserId = () => {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          return user.id || user.user_id || user.userId || '1';
        } catch {
          return '1';
        }
      };

      // Convert user-entered total quantity to base units (grams/milliliters)
      const convertToBaseUnit = (value, unitName) => {
        const val = parseFloat(value);
        const unit = unitName?.toLowerCase() || '';
        
        // Weight conversions to grams
        if (unit === 'kilogram' || unit === 'kg') return val * 1000;
        if (unit === 'gram' || unit === 'g') return val;
        if (unit === 'milligram' || unit === 'mg') return val / 1000;
        
        // Volume conversions to milliliters
        if (unit === 'liter' || unit === 'l' || unit === 'litre') return val * 1000;
        if (unit === 'milliliter' || unit === 'ml') return val;
        
        // Default: assume already in base unit
        return val;
      };

      // Check if a unit is a count-type (piece, dozen, nos, each, etc.)
      const isCountUnit = (unitName) => {
        const unit = unitName?.toLowerCase() || '';
        return ['piece', 'pieces', 'pcs', 'pc', 'nos', 'no', 'each', 'dozen', 'dz', 'unit', 'units', 'number', 'serving', 'servings', 'plate', 'plates', 'cup', 'cups', 'glass', 'glasses', 'bottle', 'bottles', 'packet', 'packets', 'pack', 'packs', 'box', 'boxes', 'can', 'cans', 'slice', 'slices', 'scoop', 'scoops'].includes(unit);
      };

      // Use the user-entered total quantity (YIELD), not sum of ingredients
      const userEnteredTotal = parseFloat(totalQuantity);
      const outputIsCount = isCountUnit(selectedUnit?.unit_name);
      const totalWeightInBaseUnits = outputIsCount ? userEnteredTotal : convertToBaseUnit(userEnteredTotal, selectedUnit?.unit_name);


      const payload = {
        businessId,
        userId: getUserId(),
        compositeName: preparationName.trim(),
        totalWeight: userEnteredTotal, // Store original value (e.g., 2, not 2000)
        totalWeightUnitId: selectedUnit?.unit_id, // Store unit reference
        components: updatedIngredients
          .filter(i => i.atomic_item_id != null) // Safety: skip any still-unresolved rows
          .map(i => {
            // When output is count-type (piece, dozen), use raw quantities to avoid
            // mixing incompatible dimensions (grams / pieces = nonsense large numbers)
            const componentQty = outputIsCount
              ? parseFloat(i.quantity)
              : convertToBaseUnit(parseFloat(i.quantity), i.unit_name);
            return {
              atomic_item_id: i.atomic_item_id,
              weight: parseFloat(i.quantity), // Store original weight
              unit_id: i.unit_id, // Store original unit
              proportion: componentQty / totalWeightInBaseUnits // Safe ratio
            };
          })
      };


      const response = await fetch(`${apiUrl}/medium-features/decomposition/learn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();

        if (activeReviewDraftKey) {
          clearReviewDraftByKey(activeReviewDraftKey);
        }

        const savedTemplateId = result?.template?.id || editingPreparationId;
        if (savedTemplateId) {
          clearReviewDraftForPreparation(savedTemplateId);
        }
        
        // Reload preparations list
        await loadExistingPreparations();
        
        // Reset form and go back to list
        setInlineEditingPreparationId(null);
        setEditingPreparationId(null);
        setActiveReviewDraftKey(null);
        setPreparationName('');
        setTotalQuantity('');
        clearIngredientEditorState();
        setStep(0);
        
        onSave && onSave(result);
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save preparation');
      }
    } catch (error) {
      console.error('Failed to save preparation:', error);
      openFeedbackAlert('Failed to Save Preparation', error.message || 'Failed to save preparation.');
    } finally {
      setSaving(false);
    }
  };

  const filterIngredients = (searchTerm) => {
    return sortBySearchPriority(
      availableIngredients,
      searchTerm,
      (item) => item?.name || '',
      { sortWhenEmpty: true, limit: 50 }
    );
  };

  const defaultUnitList = (() => {
    const desiredUnits = ['g', 'kg', 'ml', 'L', 'pcs'];
    const seen = new Set();
    const list = [];
    for (const unit of (availableUnits || [])) {
      const short = getUnitShortform(unit);
      if (desiredUnits.includes(short) && !seen.has(short)) {
        seen.add(short);
        list.push({ ...unit, shortform: short });
      }
    }
    for (const short of desiredUnits) {
      if (!seen.has(short)) {
        seen.add(short);
        list.push({
          unit_id: `unit-${short.toLowerCase()}`,
          unit_name: short,
          unit_symbol: short,
          shortform: short
        });
      }
    }
    return list.sort((a, b) => desiredUnits.indexOf(a.shortform) - desiredUnits.indexOf(b.shortform));
  })();

  const getCompatibleUnits = (currentUnit, unitsList, ingredient) => {
    // For newly entered items (not linked to an existing inventory item with a fixed base unit),
    // show all standard units (g, kg, ml, L, pcs) so the user can select any unit.
    if (!ingredient || !ingredient.atomic_item_id) {
      return defaultUnitList;
    }

    const rawSymbol = String(currentUnit?.unit_symbol || currentUnit?.unit_name || currentUnit || '').trim().toLowerCase();
    const isWeight = ['g', 'kg', 'gram', 'grams', 'kilogram', 'kilograms', 'mg'].includes(rawSymbol);
    const isVolume = ['l', 'ml', 'liter', 'liters', 'litre', 'litres', 'milliliter', 'milliliters'].includes(rawSymbol);
    const isCount = ['pcs', 'pc', 'nos', 'no', 'each', 'dozen', 'dz', 'unit', 'units'].includes(rawSymbol);

    const seen = new Set();
    const matches = [];

    for (const u of (unitsList || availableUnits || [])) {
      const short = getUnitShortform(u);
      const uSym = String(u.unit_symbol || u.unit_name || '').trim().toLowerCase();
      const uName = String(u.unit_name || '').trim().toLowerCase();

      let isCompatible = false;
      if (isWeight) {
        isCompatible = ['g', 'kg', 'gram', 'grams', 'kilogram', 'kilograms'].includes(uSym) || ['g', 'kg', 'gram', 'grams', 'kilogram', 'kilograms'].includes(uName);
      } else if (isVolume) {
        isCompatible = ['l', 'ml', 'liter', 'liters', 'litre', 'litres', 'milliliter', 'milliliters'].includes(uSym) || ['l', 'ml', 'liter', 'liters', 'litre', 'litres', 'milliliter', 'milliliters'].includes(uName);
      } else if (isCount) {
        isCompatible = ['pcs', 'pc', 'nos', 'each', 'unit'].includes(uSym) || ['piece', 'pieces', 'pcs', 'unit', 'units'].includes(uName);
      } else {
        isCompatible = ['kg', 'g', 'L', 'ml', 'pcs'].includes(short);
      }

      if (isCompatible && ['kg', 'g', 'L', 'ml', 'pcs'].includes(short)) {
        if (!seen.has(short)) {
          seen.add(short);
          matches.push({ ...u, shortform: short });
        }
      }
    }

    if (isWeight) {
      const order = ['g', 'kg'];
      matches.sort((a, b) => order.indexOf(a.shortform) - order.indexOf(b.shortform));
    } else if (isVolume) {
      const order = ['ml', 'L'];
      matches.sort((a, b) => order.indexOf(a.shortform) - order.indexOf(b.shortform));
    }

    return matches.length > 0 ? matches : defaultUnitList;
  };

  const inlineUnitMatches = defaultUnitList;
  const mainUnitMatches = defaultUnitList;

  const clearPreparationSearch = () => {
    setPreparationSearchQuery('');
    if (preparationSearchInputRef.current) {
      preparationSearchInputRef.current.focus();
    }
  };

  const filteredPreparations = useMemo(() => {
    const localReviewDraftPreparations = Object.entries(reviewDraftMap)
      .filter(([key, draft]) => key.startsWith('new:') && draft?.isUnsaved)
      .map(([key, draft]) => ({
        id: key,
        draft_key: key,
        is_local_review_draft: true,
        composite_name: draft.preparationName || 'Untitled Preparation',
        total_weight: draft.totalQuantity || '',
        total_weight_unit_name: draft.selectedUnitName || '',
        total_weight_unit_symbol: draft.selectedUnitSymbol || '',
        components: (draft.ingredients || []).map((ingredient) => ({
          atomic_item_id: ingredient.atomic_item_id,
          item_name: ingredient.item_name,
          weight: ingredient.quantity,
          unit_name: ingredient.unit_name || ingredient.unit_symbol,
        })),
      }));

    const preparationsWithSearchFields = [...localReviewDraftPreparations, ...existingPreparations].map(prep => ({
      ...prep,
      ingredients_text: (prep.components || []).map(component => (
        component.item_name || component.ingredient_name || component.name || ''
      )).join(' '),
      yield_text: `${prep.total_weight || ''} ${prep.total_weight_unit_name || prep.total_weight_unit_symbol || ''}`
    }));

    const sortByName = (list) => [...list].sort((a, b) =>
      (a.composite_name || '').localeCompare((b.composite_name || ''), undefined, { sensitivity: 'base' })
    );

    return preparationSearchQuery.trim()
      ? performSmartSearch(preparationsWithSearchFields, preparationSearchQuery, ['composite_name', 'ingredients_text', 'yield_text'])
      : sortByName(preparationsWithSearchFields);
  }, [existingPreparations, preparationSearchQuery, reviewDraftMap]);

  const hasAnyPreparationRows = useMemo(() => {
    const hasSavedPreparations = existingPreparations.length > 0;
    const hasLocalReviewDrafts = Object.entries(reviewDraftMap)
      .some(([key, draft]) => key.startsWith('new:') && draft?.isUnsaved);
    return hasSavedPreparations || hasLocalReviewDrafts;
  }, [existingPreparations, reviewDraftMap]);

  const modalContent = (
    <>
      <div className="prep-modal-overlay">
        <div
          className={`prep-modal-content ${showDeletePreparationModal || feedbackModal.isOpen ? 'pcdm-main-hidden' : ''} ${allowModalOverflow ? 'pcdm-dropdown-open' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="prep-modal-header">
          <div className="pcdm-header-row">
            <div style={{ width: 32, height: 32, flex: '0 0 auto', visibility: 'hidden' }} aria-hidden="true" />
            <h2 className="pcdm-modal-title">
              <span className={step === 2 ? 'pcdm-modal-title-recipe' : undefined}>
                {step === 0 ? 'Saved Preparations' : step === 1 ? (editingPreparationId ? 'Edit Preparation' : 'Create Custom Preparation') : toTitleCaseWords(preparationName)}
              </span>
            </h2>
            <button
              type="button"
              className="pcdm-header-close"
              onClick={() => {
                if (typeof onCancel === 'function') {
                  onCancel();
                }
              }}
              aria-label="Close modal"
              title="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Step 0: Existing Preparations List */}
        {step === 0 && (
          <div className="prep-modal-body">
            {loadingPreparations ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                Loading preparations...
              </div>
            ) : !hasAnyPreparationRows ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ color: '#999' }}>No saved preparations</div>
              </div>
            ) : (
              <>
                <StandardSearch
                  value={preparationSearchQuery}
                  onChange={setPreparationSearchQuery}
                  onClear={clearPreparationSearch}
                  placeholder="Search preparations..."
                  inputRef={preparationSearchInputRef}
                  style={{ marginBottom: '12px' }}
                />

                <div
                  className="prep-ingredients-list"
                  style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    overflowX: 'visible',
                    marginBottom: '16px'
                  }}
                >
                  {filteredPreparations.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
                      No matching preparations found
                    </div>
                  ) : filteredPreparations.map(prep => (
                    <div 
                      key={prep.id} 
                      className="prep-ingredient-row pcdm-prep-card-item"
                      onClick={() => handleEditPreparation(prep)}
                      style={{ 
                        cursor: 'pointer',
                        display: 'block'
                      }}
                    >
                      <div className="pcdm-prep-header">
                        <div className="pcdm-prep-title">
                          {inlineEditingPreparationId === prep.id ? (
                            <label className="pcdm-inline-label" style={{ marginBottom: 0 }}>Preparation Name</label>
                          ) : (
                            toTitleCaseWords(prep.composite_name)
                          )}
                          {reviewDraftMap[prep.draft_key || `prep:${prep.id}`] && (
                            <span
                              style={{
                                marginLeft: '8px',
                                fontSize: '11px',
                                fontWeight: 700,
                                color: '#92400e',
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '999px',
                                padding: '2px 8px',
                                display: 'inline-block',
                                verticalAlign: 'middle'
                              }}
                            >
                              Under Review
                            </span>
                          )}
                        </div>
                        <div className="pcdm-prep-actions">
                          <button
                            onClick={(e) => handleOpenPreparationEditor(e, prep)}
                            className="pcdm-list-edit-btn"
                            title={inlineEditingPreparationId === prep.id ? "Close editor" : "Edit preparation"}
                            type="button"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </button>
                        </div>
                      </div>

                      {inlineEditingPreparationId !== prep.id && (
                        <div className="pcdm-prep-meta-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span className="pcdm-meta-muted">
                            {prep.components?.length || 0} ingredient{(prep.components?.length || 0) !== 1 ? 's' : ''}
                          </span>
                          {prep.total_weight && (
                            <span className="pcdm-prep-yield">
                              Quantity: {formatQuantity(prep.total_weight, prep.total_weight_unit_symbol || prep.total_weight_unit_name || 'g')}
                            </span>
                          )}
                        </div>
                      )}

                      {inlineEditingPreparationId === prep.id && (
                        <div className="pcdm-inline-editor" onClick={(e) => e.stopPropagation()}>
                          <div className="pcdm-inline-fields">
                            <div className="pcdm-inline-field">
                              <input
                                type="text"
                                className="pcdm-inline-input"
                                value={preparationName}
                                onChange={(e) => setPreparationName(toTitleCaseWords(e.target.value))}
                                placeholder="Preparation name"
                              />
                            </div>
                            <div className="pcdm-inline-row">
                              <div className="pcdm-inline-field">
                                <label className="pcdm-inline-label">
                                  <span className="desktop-qty-label">Total Quantity Prepared</span>
                                  <span className="mobile-qty-label">Total Qty Prepared</span>
                                </label>
                                <input
                                  type="number"
                                  className="pcdm-inline-input"
                                  value={totalQuantity}
                                  onChange={(e) => setTotalQuantity(e.target.value)}
                                  placeholder="Total quantity prepared"
                                />
                              </div>
                              <div className="pcdm-inline-field">
                                <label className="pcdm-inline-label">Units</label>
                                <div className="pcdm-inline-unit-wrap">
                                  <div
                                    className="pcdm-inline-unit-trigger"
                                    onClick={() => {
                                      setShowInlineUnitDropdown(prev => !prev);
                                    }}
                                  >
                                    <span>{(getUnitShortform(selectedUnit) === 'L' ? 'l' : getUnitShortform(selectedUnit)) || 'Select unit'}</span>
                                    <FaChevronDown style={{ fontSize: '10px', color: '#999', transform: showInlineUnitDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                  </div>
                                  {showInlineUnitDropdown && (
                                    <div className="pcdm-inline-unit-dropdown">
                                      <div className="pcdm-inline-unit-list">
                                        {inlineUnitMatches.map(unit => (
                                          <div
                                            key={unit.unit_id}
                                            className="pcdm-inline-unit-option"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const fromSym = (getUnitShortform(selectedUnit) || '').toLowerCase();
                                              const toSym = (getUnitShortform(unit) || '').toLowerCase();
                                              const currentQty = parseFloat(totalQuantity);
                                              if (!isNaN(currentQty) && currentQty > 0) {
                                                if (fromSym === 'kg' && toSym === 'g') {
                                                  setTotalQuantity((currentQty * 1000).toString());
                                                } else if (fromSym === 'g' && toSym === 'kg') {
                                                  setTotalQuantity((currentQty / 1000).toString());
                                                } else if (fromSym === 'l' && toSym === 'ml') {
                                                  setTotalQuantity((currentQty * 1000).toString());
                                                } else if (fromSym === 'ml' && toSym === 'l') {
                                                  setTotalQuantity((currentQty / 1000).toString());
                                                }
                                              }
                                              setSelectedUnit(unit);
                                              setInlineUnitSearchTerm('');
                                              setShowInlineUnitDropdown(false);
                                            }}
                                          >
                                            {(() => {
                                              const sf = unit.shortform || getUnitShortform(unit);
                                              return sf === 'L' ? 'l' : sf;
                                            })()}
                                          </div>
                                        ))}
                                        {inlineUnitMatches.length === 0 && (
                                          <div className="pcdm-inline-unit-option pcdm-inline-unit-empty">
                                            No units found
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="prep-modal-actions pcdm-inline-actions">
                            <button
                              type="button"
                              className="prep-btn prep-btn-secondary"
                              onClick={(e) => handleOpenInlineDelete(e, prep)}
                              disabled={saving || deletingPreparation}
                            >
                              Remove
                            </button>
                            <button
                              type="button"
                              className="prep-btn prep-btn-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSave();
                              }}
                              disabled={saving || ingredients.length === 0}
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {inlineEditingPreparationId === null && (
              <div className="prep-modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
                <button className="prep-btn prep-btn-secondary" onClick={onCancel} style={{ flex: '1' }}>
                  Close
                </button>
                <button 
                  className="prep-btn prep-btn-primary" 
                  onClick={handleCreateNew}
                  style={{ flex: '1' }}
                >
                  Add Preparation
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Basic Information */}
        {step === 1 && (
          <div className="prep-modal-body">
            <div className="prep-form-group">
              <label className="prep-label">Preparation Name</label>
              <input
                type="text"
                className="prep-input"
                placeholder="e.g., White Sauce, Biryani Masala"
                value={preparationName}
                onChange={(e) => setPreparationName(toTitleCaseWords(e.target.value))}
                autoFocus
              />
            </div>

            <div className="prep-form-group">
              <div style={{ display: 'flex', gap: '12px' }}>
                {/* Left Column: Total Quantity Prepared */}
                <div style={{ flex: 1 }}>
                  <label className="prep-label" style={{textAlign:'left'}}>
                    <span className="desktop-qty-label">Total Quantity Prepared</span>
                    <span className="mobile-qty-label">Total Qty Prepared</span>
                  </label>
                  <input
                    type="number"
                    className="prep-input"
                    placeholder="e.g., 10"
                    value={totalQuantity}
                    onChange={(e) => setTotalQuantity(e.target.value)}
                  />
                </div>
                
                {/* Right Column: Units */}
                <div style={{ flex: 1 }}>
                  <label className="prep-label" style={{textAlign:'left'}}>Units</label>
                  {/* Custom Unit Dropdown with Search */}
                  <div className="prep-dropdown-container" style={{ position: 'relative' }}>
                    <div
                      ref={mainUnitSelectorRef}
                      className="prep-unit-selector-main"
                      onClick={() => {
                        setShowMainUnitDropdown(prev => !prev);
                      }}
                    >
                      <span>{(getUnitShortform(selectedUnit) === 'L' ? 'l' : getUnitShortform(selectedUnit)) || 'Select unit'}</span>
                      <FaChevronDown style={{ fontSize: '12px', color: '#999', transform: showMainUnitDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                    </div>
                  {showMainUnitDropdown && (
                    <div className="prep-main-unit-dropdown">
                      <div className="prep-unit-dropdown-list">
                        {mainUnitMatches.map(unit => (
                          <div
                            key={unit.unit_id}
                            className="prep-unit-dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              const fromSym = (getUnitShortform(selectedUnit) || '').toLowerCase();
                              const toSym = (getUnitShortform(unit) || '').toLowerCase();
                              const currentQty = parseFloat(totalQuantity);
                              if (!isNaN(currentQty) && currentQty > 0) {
                                if (fromSym === 'kg' && toSym === 'g') {
                                  setTotalQuantity((currentQty * 1000).toString());
                                } else if (fromSym === 'g' && toSym === 'kg') {
                                  setTotalQuantity((currentQty / 1000).toString());
                                } else if (fromSym === 'l' && toSym === 'ml') {
                                  setTotalQuantity((currentQty * 1000).toString());
                                } else if (fromSym === 'ml' && toSym === 'l') {
                                  setTotalQuantity((currentQty / 1000).toString());
                                }
                              }
                              setSelectedUnit(unit);
                              setShowMainUnitDropdown(false);
                              setMainUnitSearchTerm('');
                            }}
                          >
                            {(() => {
                               const sf = unit.shortform || getUnitShortform(unit);
                               return sf === 'L' ? 'l' : sf;
                             })()}
                          </div>
                        ))}
                        {mainUnitMatches.length === 0 && (
                          <div className="prep-unit-dropdown-item" style={{ color: '#999', fontStyle: 'italic', cursor: 'default' }}>
                            No units found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>

            <div className="prep-modal-actions">
              <button className="prep-btn prep-btn-secondary" onClick={handleBack}>
                Back
              </button>
              <button className="prep-btn prep-btn-primary" onClick={handleNext}>
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Add Ingredients */}
        {step === 2 && (
          <div className="prep-modal-body prep-modal-body--step2">
            <div className="prep-info-banner">
              Total: <strong>{totalQuantity} {selectedUnit?.unit_name}</strong>
            </div>

            <div className="prep-ingredients-section">
              <div className="prep-ingredients-toolbar">
                <h4 className="prep-ingredients-title">Ingredients Needed</h4>
                <div className="prep-ingredients-toolbar-row">
                  <button className="prep-btn-add" onClick={addIngredient}>
                    + Add Ingredient
                  </button>
                  <div className="prep-ingredient-toolbar-actions">
                    <button
                      type="button"
                      className={`prep-icon-btn prep-icon-btn-wide ${historyIndex < 0 ? 'is-disabled' : ''}`}
                      onClick={handleUndo}
                      disabled={historyIndex < 0}
                      title="Undo"
                    >
                      <FontAwesomeIcon icon={faUndo} style={{ fontSize: 12 }} />
                    </button>
                    <button
                      type="button"
                      className={`prep-icon-btn prep-icon-btn-wide ${historyIndex >= ingredientHistory.length - 1 ? 'is-disabled' : ''}`}
                      onClick={handleRedo}
                      disabled={historyIndex >= ingredientHistory.length - 1}
                      title="Redo"
                    >
                      <FontAwesomeIcon icon={faRedo} style={{ fontSize: 12 }} />
                    </button>
                    <button
                      type="button"
                      className={`prep-icon-btn prep-icon-btn-delete ${ingredients.length === 0 ? 'is-disabled' : ''}`}
                      onClick={handleDeleteAllIngredients}
                      disabled={ingredients.length === 0}
                      title="Delete All Ingredients"
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                    </button>
                  </div>
                </div>
              </div>

              {ingredients.length === 0 ? (
                <div className="prep-empty-state">
                  Click "Add Ingredient" to start adding components
                </div>
              ) : (
                <div
                  ref={ingredientsListRef}
                  className="pcm-ingredients-list"
                  onScroll={() => {
                    if (Object.values(showDropdowns || {}).some(Boolean) || Object.values(showUnitDropdowns || {}).some(Boolean)) {
                      setShowDropdowns({});
                      setShowUnitDropdowns({});
                      setDropdownMaxHeights({});
                      setDropdownAnchors({});
                    }
                  }}
                >
                  {ingredients.map((ingredient, index) => {
                    const rowKey = ingredient.rowId || String(index);
                    const searchDropdownMaxHeight = dropdownMaxHeights[`search-${rowKey}`];
                    const unitDropdownMaxHeight = dropdownMaxHeights[`unit-${rowKey}`];
                    const searchDropdownAnchor = dropdownAnchors[`search-${rowKey}`];
                    const unitDropdownAnchor = dropdownAnchors[`unit-${rowKey}`];
                    const isRowDropdownOpen = !!showDropdowns[rowKey] || !!showUnitDropdowns[rowKey];
                    const swipeOffset = prepSwipeOffsets[index] || 0;
                    const searchValue = (searchTerms[rowKey] || '').trim().toLowerCase();
                    const ingredientMatches = filterIngredients(searchTerms[rowKey] || '');
                    const preparationMatches = sortBySearchPriority(
                      existingPreparations,
                      searchValue,
                      (prep) => {
                        const prepName = prep?.composite_name || '';
                        const componentNames = (prep?.components || []).map((c) => c?.item_name || '').join(' ');
                        return `${prepName} ${componentNames}`;
                      },
                      { sortWhenEmpty: true, limit: 20 }
                    );
                    const unitMatches = getCompatibleUnits(ingredient.unit_symbol || ingredient.unit_name || 'g', availableUnits, ingredient);
                    return (
                    <div
                      key={rowKey}
                      className={`pcm-ingredient-row ${Math.abs(swipeOffset) > 10 ? 'pcm-swipe-active' : ''}`}
                      style={{
                        zIndex: isRowDropdownOpen ? 3 : 1,
                        transform: `translateX(${swipeOffset}px)`,
                        transition: prepSwipingIndex === index ? 'none' : 'transform 0.12s ease'
                      }}
                      onTouchStart={(e) => handlePrepTouchStart(index, e)}
                      onTouchMove={(e) => handlePrepTouchMove(index, e)}
                      onTouchEnd={() => handlePrepTouchEnd(index)}
                    >
                      {/* Ingredient Name */}
                      <div className="pcm-ingredient-name" style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className="pcm-name-input"
                          ref={(node) => {
                            if (node) ingredientNameInputRefs.current[rowKey] = node;
                            else delete ingredientNameInputRefs.current[rowKey];
                          }}
                          placeholder="Search Ingredient Name"
                          value={searchTerms[rowKey] !== undefined ? searchTerms[rowKey] : toIngredientDisplayText(ingredient.item_name)}
                          onChange={(e) => {
                            const value = e.target.value;
                            const formattedValue = toIngredientDisplayText(value);
                            setSearchTerms({ ...searchTerms, [rowKey]: formattedValue });
                            updateIngredient(index, 'item_name', formattedValue);
                            const placement = getDropdownPlacement(e.currentTarget, 220, 240, 'left', { preferBelow: true });
                            setDropdownMaxHeights(prev => ({ ...prev, [`search-${rowKey}`]: placement.maxHeight }));
                            setDropdownAnchors(prev => ({
                              ...prev,
                              [`search-${rowKey}`]: createDropdownAnchor(placement)
                            }));
                            setShowDropdowns({ [rowKey]: true });
                            setShowUnitDropdowns({});
                          }}
                          onFocus={(e) => {
                            const placement = getDropdownPlacement(e.currentTarget, 220, 240, 'left', { preferBelow: true });
                            setDropdownMaxHeights(prev => ({ ...prev, [`search-${rowKey}`]: placement.maxHeight }));
                            setDropdownAnchors(prev => ({
                              ...prev,
                              [`search-${rowKey}`]: createDropdownAnchor(placement)
                            }));
                            setShowDropdowns({ [rowKey]: true });
                            setShowUnitDropdowns({});
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const nameVal = (searchTerms[rowKey] || '').trim();
                              const nameDb = (ingredient.item_name || '').trim();
                              if (!nameVal && !nameDb) {
                                const previousList = cloneIngredientList(ingredients);
                                const nextIngredients = previousList.filter(item => item.rowId !== rowKey);
                                setIngredients(nextIngredients);
                                pushIngredientHistory(previousList, nextIngredients);
                                setShowDropdowns({});
                                handleSave(nextIngredients);
                                return;
                              }
                              setShowDropdowns({});
                              setDropdownMaxHeights(prev => {
                                const next = { ...prev };
                                delete next[`search-${rowKey}`];
                                return next;
                              });
                              setDropdownAnchors(prev => {
                                const next = { ...prev };
                                delete next[`search-${rowKey}`];
                                return next;
                              });
                              focusIngredientQuantityInput(rowKey);
                            }
                          }}
                        />
                        {showDropdowns[rowKey] && (
                          searchDropdownAnchor && typeof document !== 'undefined' && ReactDOM.createPortal(
                            <div
                              className="pcm-suggestion-dropdown pcm-dropdown-portal"
                              ref={(node) => syncDropdownAnchorToRenderedHeight(`search-${rowKey}`, node, searchDropdownMaxHeight)}
                              style={{
                                top: `${searchDropdownAnchor.top}px`,
                                left: `${searchDropdownAnchor.left}px`,
                                width: `${searchDropdownAnchor.width}px`,
                                ...(searchDropdownMaxHeight ? { maxHeight: `${searchDropdownMaxHeight}px` } : {})
                              }}
                            >
                              {ingredientMatches.length > 0 ? (
                                ingredientMatches.map(item => (
                                <div
                                  key={item.item_id}
                                  className="pcm-suggestion-item"
                                  onClick={() => {
                                    const previousList = cloneIngredientList(ingredients);
                                    const updated = cloneIngredientList(ingredients);
                                    updated[index].atomic_item_id = item.item_id;
                                    updated[index].item_name = toIngredientNormalized(item.name);
                                    updated[index].unit_name = item.unit_name || item.unit;
                                    updated[index].unit_symbol = item.unit_symbol || item.unit;
                                    const matchingUnit = availableUnits?.find(u => 
                                      u.unit_symbol === (item.unit_symbol || item.unit) || 
                                      u.unit_name === (item.unit_name || item.unit)
                                    );
                                    if (matchingUnit) {
                                      updated[index].unit_id = matchingUnit.unit_id;
                                    }
                                    setIngredients(updated);
                                    pushIngredientHistory(previousList, updated);
                                    setSearchTerms({ ...searchTerms, [rowKey]: toIngredientNormalized(item.name) });
                                    setShowDropdowns({});
                                    setDropdownMaxHeights(prev => {
                                      const next = { ...prev };
                                      delete next[`search-${rowKey}`];
                                      return next;
                                    });
                                    setDropdownAnchors(prev => {
                                      const next = { ...prev };
                                      delete next[`search-${rowKey}`];
                                      return next;
                                    });
                                    focusIngredientQuantityInput(rowKey);
                                  }}
                                >
                                  <span className="pcm-suggestion-name">{toIngredientNormalized(item.name)}</span>
                                  <span className="pcm-suggestion-unit">
                                    ({item.unit_symbol || item.unit_name || item.unit || 'unit'})
                                  </span>
                                </div>
                              ))
                              ) : (
                                <div className="pcm-suggestion-item pcm-suggestion-item-empty">
                                  {availableIngredients.length === 0 ? 'Loading ingredients...' : 'No matching ingredients found'}
                                </div>
                              )}
                              {searchTerms[rowKey] && searchTerms[rowKey].trim().length > 0 && 
                               ingredientMatches.length === 0 && (
                                <div
                                  className="pcm-suggestion-item pcm-suggestion-create"
                                  onClick={() => createNewIngredient(searchTerms[rowKey].trim(), index)}
                                >
                                  {creatingIngredient ? 'Adding...' : `+ Add "${searchTerms[rowKey]}"`}
                                </div>
                              )}
                              {preparationMatches.length > 0 && (
                                <>
                                  <div className="pcm-suggestion-section">
                                    Saved Preparations (Import Ingredients)
                                  </div>
                                  {preparationMatches.map(prep => (
                                    <div
                                      key={prep.id || prep.composite_name}
                                      className="pcm-suggestion-item pcm-suggestion-import"
                                      onClick={() => importIngredientsFromPreparation(prep, ingredient.rowId, index)}
                                    >
                                      <span className="pcm-suggestion-name">
                                        {prep.composite_name
                                          ? toTitleCaseWords(prep.composite_name)
                                          : 'Saved Preparation'}
                                      </span>
                                      <span className="pcm-suggestion-tag">
                                        {importingPreparation ? 'IMPORTING...' : 'IMPORT'}
                                      </span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>,
                            document.body
                          )
                        )}
                      </div>

                      {/* Controls: − Qty ＋ Unit Delete (same as itemmap) */}
                      <div className="pcm-controls">
                        <button
                          className="pcm-square-btn"
                          onClick={() => {
                            const current = Number(ingredient.quantity) || 0;
                            updateIngredient(index, 'quantity', Math.max(0, current - 1));
                          }}
                        >−</button>
                        <input
                          type="number"
                          className="pcm-qty-input"
                          ref={(node) => {
                            if (node) ingredientQtyInputRefs.current[rowKey] = node;
                            else delete ingredientQtyInputRefs.current[rowKey];
                          }}
                          placeholder="0"
                          value={ingredient.quantity}
                          onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addIngredient();
                            }
                          }}
                        />
                        <button
                          className="pcm-square-btn"
                          onClick={() => {
                            const current = Number(ingredient.quantity) || 0;
                            updateIngredient(index, 'quantity', current + 1);
                          }}
                        >＋</button>

                        {/* Unit Dropdown */}
                        <div className="pcm-unit-wrapper" style={{ position: 'relative' }}>
                          <div
                            className="pcm-unit-trigger"
                            onClick={(e) => {

                              const willOpen = !showUnitDropdowns[rowKey];
                              if (!willOpen) {
                                setShowUnitDropdowns({});
                                setDropdownMaxHeights(prev => {
                                  const next = { ...prev };
                                  delete next[`unit-${rowKey}`];
                                  return next;
                                });
                                setDropdownAnchors(prev => {
                                  const next = { ...prev };
                                  delete next[`unit-${rowKey}`];
                                  return next;
                                });
                                return;
                              }

                              const placement = getDropdownPlacement(e.currentTarget, 140, 76, 'right');
                              setDropdownMaxHeights(prev => ({ ...prev, [`unit-${rowKey}`]: placement.maxHeight }));
                              setDropdownAnchors(prev => ({
                                ...prev,
                                [`unit-${rowKey}`]: createDropdownAnchor(placement)
                              }));
                              setShowUnitDropdowns({ [rowKey]: true });
                              setShowDropdowns({});
                            }}
                          >
                            <span>{(() => {
                               const symbol = ingredient.unit_symbol || ingredient.unit_name || 'g';
                               return symbol === 'L' ? 'l' : symbol;
                             })()}</span>
                            <FaChevronDown style={{ fontSize: '10px', color: '#999' }} />
                          </div>
                          {showUnitDropdowns[rowKey] && (
                            unitDropdownAnchor && typeof document !== 'undefined' && ReactDOM.createPortal(
                            <div
                              className="pcm-unit-dropdown pcm-dropdown-portal"
                              ref={(node) => syncDropdownAnchorToRenderedHeight(`unit-${rowKey}`, node, unitDropdownMaxHeight)}
                              style={{
                                top: `${unitDropdownAnchor.top}px`,
                                left: `${unitDropdownAnchor.left}px`,
                                width: `${unitDropdownAnchor.width}px`,
                                ...(unitDropdownMaxHeight ? { maxHeight: `${unitDropdownMaxHeight}px` } : {})
                              }}
                            >
                              <div
                                className="pcm-unit-list"
                                style={unitDropdownMaxHeight ? { maxHeight: `${unitDropdownMaxHeight}px` } : undefined}
                              >
                                {unitMatches.map(unit => (
                                  <div
                                    key={unit.unit_id}
                                    className="pcm-unit-option"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const previousList = cloneIngredientList(ingredients);
                                      const updated = cloneIngredientList(ingredients);
                                      // Bidirectional unit conversion
                                      const fromSymbol = (ingredient.unit_symbol || ingredient.unit_name || '').trim().toLowerCase();
                                      const toSymbol = (unit.unit_symbol || '').trim().toLowerCase();
                                      const currentQty = parseFloat(ingredients[index]?.quantity) || 0;
                                      let convertedQty = currentQty;
                                      if (fromSymbol !== toSymbol) {
                                        if (fromSymbol === 'g' && toSymbol === 'kg') {
                                          convertedQty = currentQty / 1000;
                                        } else if (fromSymbol === 'kg' && toSymbol === 'g') {
                                          convertedQty = currentQty * 1000;
                                        } else if (fromSymbol === 'ml' && toSymbol === 'l') {
                                          convertedQty = currentQty / 1000;
                                        } else if (fromSymbol === 'l' && toSymbol === 'ml') {
                                          convertedQty = currentQty * 1000;
                                        }
                                      }
                                      // Round to avoid floating point noise (e.g. 0.001000000001)
                                      convertedQty = parseFloat(convertedQty.toPrecision(10));
                                      updated[index].unit_id = unit.unit_id;
                                      updated[index].unit_name = unit.unit_name;
                                      updated[index].unit_symbol = unit.unit_symbol;
                                      updated[index].quantity = convertedQty.toString();
                                      setIngredients(updated);
                                      pushIngredientHistory(previousList, updated);
                                      setShowUnitDropdowns({});
                                      setUnitSearchTerms({ ...unitSearchTerms, [rowKey]: '' });
                                      setDropdownMaxHeights(prev => {
                                        const next = { ...prev };
                                        delete next[`unit-${rowKey}`];
                                        return next;
                                      });
                                      setDropdownAnchors(prev => {
                                        const next = { ...prev };
                                        delete next[`unit-${rowKey}`];
                                        return next;
                                      });
                                    }}
                                  >
                                    <span>{unit.unit_symbol === 'L' ? 'l' : unit.unit_symbol}</span>
                                  </div>
                                ))}
                              </div>
                              </div>,
                              document.body
                              )
                          )}
                        </div>

                        {/* Delete button (visible on desktop, swipe on mobile) */}
                        <button
                          className="pcm-delete-btn"
                          onClick={() => removeIngredient(ingredient.rowId, index)}
                          title="Delete"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>

            <div className="prep-modal-actions">
              <button className="prep-btn prep-btn-secondary" onClick={handleBack}>
                Back
              </button>
              <button
                className="prep-btn prep-btn-secondary"
                onClick={handleMarkAsReview}
                disabled={saving}
              >
                Review
              </button>
              <button
                className="prep-btn prep-btn-primary"
                onClick={handleSave}
                disabled={saving || ingredients.length === 0}
              >
                {saving ? 'Saving...' : editingPreparationId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        )}

        </div>
      </div>

      {feedbackModal.isOpen && (
        <div className="pcdm-delete-overlay" onClick={closeFeedbackModal}>
          <div className="pcdm-delete-modal pcdm-feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcdm-dialog-copy-block">
              <h3 className="pcdm-delete-title">{feedbackModal.title}</h3>
              <p className="pcdm-delete-text pcdm-feedback-text">{feedbackModal.message}</p>
            </div>
            <div className={`pcdm-delete-actions ${feedbackModal.mode === 'alert' ? 'pcdm-delete-actions--single' : ''}`}>
              {feedbackModal.mode === 'confirm' && (
                <button
                  type="button"
                  className="pcdm-delete-cancel"
                  onClick={closeFeedbackModal}
                >
                  {feedbackModal.cancelLabel}
                </button>
              )}
              <button
                type="button"
                className="pcdm-delete-confirm"
                onClick={handleFeedbackConfirm}
              >
                {feedbackModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUnitWarning && (
        <div className="pcdm-delete-overlay" onClick={() => setShowUnitWarning(false)}>
          <div className="pcdm-delete-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="pcdm-dialog-copy-block">
              <h3 className="pcdm-delete-title" style={{ color: '#f59e0b' }}>⚠️ Unit Mismatch</h3>
              <p className="pcdm-delete-text">
                The following ingredients have units that couldn't be resolved. Please re-select their units before saving:
              </p>
            </div>
            <ul style={{ textAlign: 'left', margin: '0 auto 12px', padding: '0 16px', fontSize: 14 }}>
              {unitWarningItems.map((item, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  <strong>{item.item_name}</strong>
                  <span style={{ color: '#888' }}> — unit "{item.unit_name}" not found</span>
                </li>
              ))}
            </ul>
            <div className="pcdm-delete-actions pcdm-delete-actions--single">
              <button
                type="button"
                className="pcdm-delete-cancel"
                onClick={() => setShowUnitWarning(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeletePreparationModal && (
        <div className="pcdm-delete-overlay" onClick={() => {
          if (deletingPreparation) return;
          setShowDeletePreparationModal(false);
          setPreparationToDelete(null);
        }}>
          <div className="pcdm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcdm-dialog-copy-block">
              <h3 className="pcdm-delete-title">Delete Preparation</h3>
              <p className="pcdm-delete-text">
                Are you sure you want to delete
                <span className="pcdm-delete-name"> {toTitleCaseWords(preparationToDelete?.composite_name || 'this preparation')}</span>?
              </p>
            </div>
            <div className="pcdm-delete-actions">
              <button
                type="button"
                className="pcdm-delete-cancel"
                onClick={() => {
                  setShowDeletePreparationModal(false);
                  setPreparationToDelete(null);
                }}
                disabled={deletingPreparation}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pcdm-delete-confirm"
                onClick={confirmDeletePreparation}
                disabled={deletingPreparation}
              >
                {deletingPreparation ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {duplicateError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2147483010
        }}>
          <div style={{
            width: 'min(380px, 90vw)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '36px', color: '#eab308' }} />
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#1f2937',
              fontFamily: 'Roboto, sans-serif'
            }}>
              Item Already Exists
            </div>
            <div style={{
              fontSize: '14px',
              color: '#4b5563',
              marginBottom: '20px',
              lineHeight: '1.4',
              fontFamily: 'Roboto, sans-serif'
            }}>
              The preparation "{duplicateError.name}" already exists. Please choose a different name.
            </div>
            <button 
              style={{
                padding: '10px 24px',
                backgroundColor: '#000000',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '120px',
                fontFamily: 'Roboto, sans-serif'
              }}
              onClick={() => setDuplicateError(null)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PreparationCreatorModal;
